import { Decimal } from 'decimal.js';
import { Tank } from 'src/types/ViewModel/Vessel/Tank';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { RotationCargoOperation } from 'src/types/ViewModel/Stowage/Rotation/RotationCargoOperation';
import { TankPlan } from 'src/types/ViewModel/Vessel/TankPlan';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { StowageRuleConsequenceType } from 'src/enums/stowage/stowageRule/stowageRuleConsequenceType';
import { CoatingCompatibility } from 'src/types/ViewModel/Stowage/Product/CoatingCompatibility';
import { UscgCompatibilitiesBM } from 'src/types/BusinessModel/Stowage/Product/UscgCompatibility/UscgCompatibilitiesBM';
import { StowagePlanRulesManager, StowageRulesOptions } from '../stowageRulesManager/stowagePlanRulesManager';
import { getCargoOperationForCargo } from '../stowageRotationUtils';
import { getAllTanks } from '../tankPlanUtils';
import { Stowage } from 'src/types/ViewModel/Stowage/Stowage';
import { updateUnstowOperationsForCargo } from '../stowageOperation/autoUnstowUtils';
import { updateManifest } from '../manifest/cargoManifestUtils';
import { getAddedViolations } from '../violations/violationsUtils';
import { Violation } from 'src/types/ViewModel/Stowage/Violation/Violation';
import { getTankContentsInPortCall } from '../manifest/cargoManifestUtils';

/**
 * Information about an unavailable tank and the violations preventing its use
 */
export interface UnavailableTankInfo {
    tank: Tank;
    violations: Violation[];
}

/**
 * Result from getUnavailableTanks containing both tank list and violation details
 */
export interface UnavailableTanksResult {
    tanks: Tank[];
    tankViolations: Map<number, Violation[]>;
}

/**
 * Parameters for getting unavailable tanks for loading operations
 */
export interface UnavailableTanksParams {
    activeStowagePlan: StowagePlan;
    cargoOperation: RotationCargoOperation;
    tankPlan: TankPlan;
    selectedVessel: Vessel;
    violations: Violation[];
    coatingCompatibilities: CoatingCompatibility[];
    uscgCompatibilities: UscgCompatibilitiesBM;
}

/**
 * Parameters for getting unavailable tanks for unloading operations
 */
export interface UnloadingTanksParams {
    activeStowagePlan: StowagePlan;
    cargoOperation: RotationCargoOperation;
    tankPlan: TankPlan;
}

/**
 * Get unavailable tanks for a loading operation
 * Uses stowage rules validation to determine which tanks cannot be used
 * @param params - Parameters containing stowage plan, cargo operation, vessel, and compatibility data
 * @returns Object containing array of unavailable tanks and a map of tankId to violations
 */
export const getUnavailableTanks = (params: UnavailableTanksParams): UnavailableTanksResult => {
    const { activeStowagePlan, cargoOperation, tankPlan, selectedVessel, violations, coatingCompatibilities, uscgCompatibilities } = params;

    const cargoOps = getCargoOperationForCargo(activeStowagePlan, cargoOperation.cargo);
    const lastPortCall = cargoOps.at(-1)?.berthCall?.portCall;

    const manager = new StowagePlanRulesManager();

    const options: StowageRulesOptions = {
        excludeSoftRuleViolations: true,
        excludePreviousPortCalls: true,
        excludeAfterLastPortCall: true,
    };

    const allTanks = getAllTanks(tankPlan);

    const simulatedPlan = {
        ...activeStowagePlan,
        stowageItems: [...activeStowagePlan.stowageItems],
    };

    const template = { cargoOperation, cargoOperationGuid: cargoOperation.cargoOperationGuid, weight: new Decimal(1) };
    const stowageItemsForAllTanks = allTanks.map((tank) => ({ ...template, tank, tankId: tank.tankId } as Stowage));

    simulatedPlan.stowageItems.push(...stowageItemsForAllTanks);

    updateUnstowOperationsForCargo(simulatedPlan, cargoOperation);

    updateManifest(simulatedPlan, selectedVessel);

    const verifiedStowageViolations = manager.verifyStowageRules(
        options,
        simulatedPlan,
        selectedVessel,
        cargoOperation.berthCall.portCall,
        lastPortCall,
        coatingCompatibilities,
        uscgCompatibilities
    );

    simulatedPlan.stowageItems = simulatedPlan.stowageItems.slice(0, simulatedPlan.stowageItems.length - stowageItemsForAllTanks.length);

    const hardRuleViolations = verifiedStowageViolations.filter((v) => v.type === StowageRuleConsequenceType.HardRule);

    // Filter to only violations for the specific cargo being dragged
    const addedViolations = getAddedViolations(violations, hardRuleViolations).filter((v) => v.tank && v.cargoGuid === cargoOperation.cargoGuid);

    // Build a map of tankId to violations for that tank
    const tankViolations = new Map<number, Violation[]>();
    const unavailableTankSet = new Set<Tank>();

    for (const violation of addedViolations) {
        if (violation.tank) {
            unavailableTankSet.add(violation.tank);
            const tankId = violation.tank.tankId;
            if (!tankViolations.has(tankId)) {
                tankViolations.set(tankId, []);
            }
            tankViolations.get(tankId)!.push(violation);
        }
    }

    return {
        tanks: Array.from(unavailableTankSet),
        tankViolations,
    };
};

/**
 * Get unavailable tanks for an unloading operation
 * For unloading, marks all tanks as unavailable except those containing the specific cargo in the current port call
 * @param params - Parameters containing stowage plan, cargo operation, and port call information
 * @returns Array of tanks that are unavailable for unloading
 */
export const getUnavailableTanksForUnloading = (params: UnloadingTanksParams): Tank[] => {
    const { activeStowagePlan, cargoOperation, tankPlan } = params;

    const allTanks = getAllTanks(tankPlan);

    // Find the current port call for this cargo operation
    const currentPortCall = activeStowagePlan?.rotation?.find((pc) => pc.portCallGuid === cargoOperation.portCallGuid);
    if (!currentPortCall) {
        // If we can't find the port call, return no unavailable tanks
        return [];
    }

    // Get tanks that contain the specific cargo in the current port call only
    const tanksWithCargoInCurrentPort: Tank[] = [];
    for (const tank of allTanks) {
        const tankContentsInPortCall = getTankContentsInPortCall(activeStowagePlan, currentPortCall, tank);
        const hasCargoInThisPort = tankContentsInPortCall.some((tankContent) =>
            tankContent.cargoContents?.some((cargoContent) => cargoContent.cargo?.cargoGuid === cargoOperation.cargoGuid)
        );
        if (hasCargoInThisPort) {
            tanksWithCargoInCurrentPort.push(tank);
        }
    }

    // Create a set of tank IDs that contain the cargo in the current port call
    const tanksWithCargoIds = new Set(tanksWithCargoInCurrentPort.map((tank) => tank.tankId));

    // Return all tanks that DON'T contain the cargo in the current port call (making them unavailable for unloading)
    return allTanks.filter((tank) => !tanksWithCargoIds.has(tank.tankId));
};
